import {
  useCinematicStore,
  DialogueView,
  ShowcaseView,
} from '../store/cinematicSlice';
import {
  CinematicDefinition,
  CinematicStep,
  Easing,
  Vec2,
  ActorRef,
  ShowcaseUnlock,
} from '../data/cinematics/types';
import { getCinematicScene } from '../cinematic/sceneAdapter';
import { directionFromDelta } from '../cinematic/direction.utils';
import { msToTicks } from '../utils/timing.utils';
import { ITEMS } from '../data/inventory.config';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import { SKETCH_REGISTRY } from '../data/sketches.config';

function resolveShowcase(unlock: ShowcaseUnlock): ShowcaseView | null {
  switch (unlock.kind) {
    case 'item': {
      const it = ITEMS[unlock.id];
      return it ? { label: it.label, sprite: it.sprite } : null;
    }
    case 'major': {
      const t = TRINKET_MAJOR_REGISTRY[
        unlock.id as keyof typeof TRINKET_MAJOR_REGISTRY
      ] as { name: string; icon: string } | undefined;
      return t ? { label: t.name, sprite: t.icon } : null;
    }
    case 'minor': {
      const t = TRINKET_MINOR_REGISTRY[
        unlock.id as keyof typeof TRINKET_MINOR_REGISTRY
      ] as { name: string; icon: string } | undefined;
      return t ? { label: t.name, sprite: t.icon } : null;
    }
    case 'sketch': {
      const s = SKETCH_REGISTRY[unlock.id as keyof typeof SKETCH_REGISTRY] as
        | { name: string; icon: string }
        | undefined;
      return s ? { label: s.name, sprite: s.icon } : null;
    }
  }
}

interface Tween {
  elapsed: number;
  duration: number;
  ease: (t: number) => number;
  onUpdate: (t: number) => void;
  resolve: () => void;
}

const EASINGS: Record<Easing, (t: number) => number> = {
  linear: (t) => t,
  'ease-in-out': (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2),
};

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

class CinematicService {
  private running = false;
  private tweens: Tween[] = [];

  private dialogueResolve: (() => void) | null = null;
  private showcaseResolve: (() => void) | null = null;
  private showcaseQueue: Array<{ view: ShowcaseView; resolve: () => void }> =
    [];
  private showcaseActive = false;
  private drainResolvers: Array<() => void> = [];

  /**
   * Resolves once no showcase is displayed and the queue is empty. Lets a
   * caller (end-of-run transition) hold black until unlock showcases finish.
   */
  awaitShowcasesDrained(): Promise<void> {
    if (!this.showcaseResolve && this.showcaseQueue.length === 0) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.drainResolvers.push(resolve);
    });
  }

  /** Advanced every frame by the active scene, even while gameplay is frozen. */
  tick(delta: number): void {
    if (this.tweens.length === 0) {
      return;
    }
    const done: Tween[] = [];
    for (const tw of this.tweens) {
      tw.elapsed += delta;
      const raw = tw.duration <= 0 ? 1 : Math.min(1, tw.elapsed / tw.duration);
      tw.onUpdate(tw.ease(raw));
      if (raw >= 1) {
        done.push(tw);
      }
    }
    if (done.length) {
      this.tweens = this.tweens.filter((tw) => !done.includes(tw));
      done.forEach((tw) => tw.resolve());
    }
  }

  async play(def: CinematicDefinition | CinematicStep[]): Promise<void> {
    if (this.running) {
      return;
    }
    this.running = true;
    const store = useCinematicStore.getState();
    store.setActive(true);
    try {
      const steps = Array.isArray(def) ? def : def.steps;
      await this.runStep({ type: 'sequence', steps });
    } finally {
      const scene = getCinematicScene();
      scene?.camera.setZoom(1);
      scene?.camera.setFollow('player');
      this.clearTransient();
      store.setActive(false);
      this.running = false;
    }
  }

  async showDialogue(view: DialogueView): Promise<void> {
    await this.ensureActive(() => this.dialogueStep(view));
  }

  showcaseItem(itemId: string): void {
    this.showcaseUnlock({ kind: 'item', id: itemId });
  }

  /** Fire-and-forget showcase of an unlocked item/trinket/sketch. */
  showcaseUnlock(unlock: ShowcaseUnlock): void {
    const view = resolveShowcase(unlock);
    if (!view) {
      return;
    }
    if (!this.running && !this.showcaseActive) {
      this.showcaseActive = true;
      useCinematicStore.getState().setActive(true);
    }
    void this.enqueueShowcase(view);
  }

  /** Called by DialogueScreen when the line is advanced/auto-dismissed. */
  resolveDialogue(): void {
    useCinematicStore.getState().setDialogue(null);
    const r = this.dialogueResolve;
    this.dialogueResolve = null;
    r?.();
  }

  /** Called by ItemShowcaseScreen on CONFIRM. */
  dismissShowcase(): void {
    const r = this.showcaseResolve;
    this.showcaseResolve = null;
    r?.();
    this.advanceShowcaseQueue();
  }

  private clearTransient(): void {
    const store = useCinematicStore.getState();
    store.setDialogue(null);
    store.setShowcase(null);
    this.dialogueResolve = null;
    this.showcaseResolve = null;
  }

  private async ensureActive(body: () => Promise<void>): Promise<void> {
    if (this.running) {
      await body();
      return;
    }
    const store = useCinematicStore.getState();
    store.setActive(true);
    try {
      await body();
    } finally {
      this.clearTransient();
      store.setActive(false);
    }
  }

  private async runStep(step: CinematicStep): Promise<void> {
    switch (step.type) {
      case 'sequence':
        for (const s of step.steps) {
          await this.runStep(s);
        }
        return;
      case 'parallel':
        await Promise.all(step.steps.map((s) => this.runStep(s)));
        return;
      case 'dialogue':
        await this.dialogueStep(step);
        return;
      case 'showcase':
        await this.showcaseStepItem(step.itemId);
        return;
      case 'wait':
        await this.tween(step.ms, 'linear', () => {});
        return;
      case 'camera-move':
        await this.cameraMoveStep(step.to, step.durationMs, step.easing);
        return;
      case 'camera-zoom':
        await this.cameraZoomStep(step.zoom, step.durationMs, step.easing);
        return;
      case 'camera-follow':
        getCinematicScene()?.camera.setFollow(step.actor);
        return;
      case 'move-actor':
        await this.moveActorStep(
          step.actor,
          step.to,
          step.durationMs,
          step.easing,
        );
        return;
    }
  }

  private dialogueStep(view: DialogueView): Promise<void> {
    return new Promise<void>((resolve) => {
      this.dialogueResolve = resolve;
      useCinematicStore.getState().setDialogue(view);
    });
  }

  private showcaseStepItem(itemId: string): Promise<void> {
    const view = resolveShowcase({ kind: 'item', id: itemId });
    if (!view) {
      return Promise.resolve();
    }
    return this.enqueueShowcase(view);
  }

  private enqueueShowcase(view: ShowcaseView): Promise<void> {
    return new Promise<void>((resolve) => {
      this.showcaseQueue.push({ view, resolve });
      if (!this.showcaseResolve) {
        this.advanceShowcaseQueue();
      }
    });
  }

  private advanceShowcaseQueue(): void {
    const next = this.showcaseQueue.shift();
    if (!next) {
      useCinematicStore.getState().setShowcase(null);
      this.showcaseResolve = null;
      if (this.showcaseActive && !this.running) {
        this.showcaseActive = false;
        useCinematicStore.getState().setActive(false);
      }
      const resolvers = this.drainResolvers;
      this.drainResolvers = [];
      resolvers.forEach((r) => r());
      return;
    }
    this.showcaseResolve = next.resolve;
    useCinematicStore.getState().setShowcase(next.view);
  }

  private resolveVec(to: Vec2 | ActorRef): Vec2 | null {
    if (typeof to === 'object' && 'x' in to) {
      return to;
    }
    const actor = getCinematicScene()?.getActor(to);
    return actor ? actor.getPosition() : null;
  }

  private cameraMoveStep(
    to: Vec2 | ActorRef,
    durationMs: number,
    easing?: Easing,
  ): Promise<void> {
    const cam = getCinematicScene()?.camera;
    const target = this.resolveVec(to);
    if (!cam || !target) {
      return Promise.resolve();
    }
    cam.setFollow(null);
    const start = cam.getPosition();
    return this.tween(durationMs, easing ?? 'ease-in-out', (t) =>
      cam.setPosition({
        x: lerp(start.x, target.x, t),
        y: lerp(start.y, target.y, t),
      }),
    );
  }

  private cameraZoomStep(
    zoom: number,
    durationMs: number,
    easing?: Easing,
  ): Promise<void> {
    const cam = getCinematicScene()?.camera;
    if (!cam) {
      return Promise.resolve();
    }
    const start = cam.getZoom();
    return this.tween(durationMs, easing ?? 'ease-in-out', (t) =>
      cam.setZoom(lerp(start, zoom, t)),
    );
  }

  private moveActorStep(
    ref: ActorRef,
    to: Vec2,
    durationMs: number,
    easing?: Easing,
  ): Promise<void> {
    const actor = getCinematicScene()?.getActor(ref);
    if (!actor) {
      return Promise.resolve();
    }
    const start = actor.getPosition();
    actor.setFacing(directionFromDelta(to.x - start.x, to.y - start.y));
    actor.playWalk();
    return this.tween(durationMs, easing ?? 'linear', (t) =>
      actor.setPosition({
        x: lerp(start.x, to.x, t),
        y: lerp(start.y, to.y, t),
      }),
    ).then(() => actor.playIdle());
  }

  private tween(
    durationMs: number,
    easing: Easing,
    onUpdate: (t: number) => void,
  ): Promise<void> {
    return new Promise<void>((resolve) => {
      this.tweens.push({
        elapsed: 0,
        duration: msToTicks(durationMs),
        ease: EASINGS[easing],
        onUpdate,
        resolve,
      });
    });
  }
}

export const cinematicService = new CinematicService();
